import { OrderSearchErrors } from "@redotech/customer-portal-sdk/rpc/schema/get-orders-from-search";
import { SendEmailAuthLinkError } from "@redotech/customer-portal-sdk/rpc/schema/send-email-auth-link";
import { SingleOrderAuthError } from "@redotech/customer-portal-sdk/rpc/schema/single-order-auth";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { getResource } from "@redotech/redo-model/localization/resource";
import { ReturnType, ReturnTypeEnum } from "@redotech/redo-model/return";
import { teamOffersExchanges } from "@redotech/redo-model/return-flow";
import { Settings } from "@redotech/redo-model/team";

export enum HeaderText {
  RETURNS_ONLY = "Returns",
  CLAIMS_ONLY = "Claims",
  RETURNS_EXCHANGES = "Returns & Exchanges",
  RETURNS_CLAIMS = "Returns & Claims",
  RETURNS_CLAIMS_WARRANTIES = "Returns, Claims, & Warranties",
  RETURNS_WARRANTIES = "Returns & Warranties",
  WARRANTIES_ONLY = "Warranties",
}

export const orderSearchErrorToText: Record<OrderSearchErrors, string> = {
  [OrderSearchErrors.NO_EMAIL]: "No email found for this account",
  [OrderSearchErrors.INVALID_SEARCH]: "Invalid search parameters provided",
  [OrderSearchErrors.NEW_AUTH_ONLY]:
    "This endpoint may only be used with new auth",
  [OrderSearchErrors.ORDERLESS_UNSUPPORTED]:
    "Orderless auth scope is not supported",
  [OrderSearchErrors.INTERNAL_ERROR]:
    "Something went wrong on our end, please try again.",
};

export const singleOrderAuthErrorToText: Record<SingleOrderAuthError, string> =
  {
    [SingleOrderAuthError.INVALID_EMAIL_OR_ZIP]:
      "Invalid email or zip or order-number or tracking-number",
    [SingleOrderAuthError.MUST_PROVIDE_EMAIL_OR_ZIP]:
      "You must provide either an email or a zip code",
  };
export const sendEmailAuthLinkErrorToText: Record<
  SendEmailAuthLinkError,
  string
> = {
  [SendEmailAuthLinkError.InvalidEmail]:
    "The email we tried to send to is either inactive or marked as spam, please try again with a different email or contact support to update your email.",
  [SendEmailAuthLinkError.UnknownError]:
    "Something went wrong sending the email.",
};
export const ButtonText = (settings: CustomerPortalAppSettings) => {
  return {
    RETURNS_ONLY: getResource({
      variant: "return",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
    CLAIMS_ONLY: getResource({
      variant: "claim",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
    RETURNS_EXCHANGES: getResource({
      variant: "exchange",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
    RETURNS_CLAIMS: getResource({
      variant: "return_claim",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
  };
};

const teamOffersPackageProtection = (
  settings: CustomerPortalAppSettings | undefined,
): boolean => {
  return (
    (settings?.packageProtection?.enabled &&
      settings.packageProtection?.customerClaimsEnabled) === true
  );
};
const teamOffersReturns = (
  settings: CustomerPortalAppSettings | undefined,
): boolean => {
  return !!settings?.returns?.enabled;
};

const teamOffersWarranties = (
  settings: CustomerPortalAppSettings | undefined,
): boolean => {
  return !!settings?.warranties?.enabled;
};

export const getMissingOrderHeaderText = (
  settings: CustomerPortalAppSettings,
) => {
  return settings.returnPortalMissingOrderHeaderText ?? getHeaderText(settings);
};

export const getHeaderText = (
  settings: CustomerPortalAppSettings,
  isCreateGiftReturnLink: boolean = false,
) => {
  if (isCreateGiftReturnLink) {
    return "Gift receipts";
  }

  if (settings?.returnPortalHeaderText) {
    return settings.returnPortalHeaderText;
  }

  const returns = teamOffersReturns(settings);
  const exchanges = teamOffersExchanges(settings.returnFlow);
  const packageProtection = teamOffersPackageProtection(settings);
  const warranties = teamOffersWarranties(settings);
  // Ordering is important
  if (returns && packageProtection && warranties) {
    return HeaderText.RETURNS_CLAIMS_WARRANTIES;
  }
  if (returns && packageProtection) {
    return HeaderText.RETURNS_CLAIMS;
  }
  if (returns && exchanges) {
    return HeaderText.RETURNS_EXCHANGES;
  }
  if (returns && warranties) {
    return HeaderText.RETURNS_WARRANTIES;
  }
  if (packageProtection) {
    return HeaderText.CLAIMS_ONLY;
  }
  if (warranties) {
    return HeaderText.WARRANTIES_ONLY;
  }

  return HeaderText.RETURNS_ONLY;
};

export const getLoginButtonText = (settings: Settings) => {
  if (settings.returnPortalButtonText) {
    return settings.returnPortalButtonText;
  }
  if (
    settings.warranties?.registrationEnabled &&
    settings.warranties?.customersCanStartClaims
  ) {
    return "Search";
  }
  return "Find order";
};

export const getCantFindOrderText = (settings: CustomerPortalAppSettings) => {
  return settings.theme?.cantFindOrderText ?? "Can't find order number?";
};

export const getStartReturnButtonText = (
  settings: CustomerPortalAppSettings,
) => {
  if (settings.theme?.returnButtonText) {
    return settings.theme.returnButtonText;
  }
  if (teamOffersExchanges(settings?.returnFlow)) {
    return ButtonText(settings).RETURNS_EXCHANGES;
  }

  return ButtonText(settings).RETURNS_ONLY;
};

export const getPolicyLinkText = (
  settings: CustomerPortalAppSettings | undefined,
) => {
  const returns = teamOffersReturns(settings);
  const packageProtection = teamOffersPackageProtection(settings);
  const variant =
    returns && packageProtection
      ? "return_claim"
      : packageProtection
        ? "claim"
        : "return";

  return getResource({ overrides: settings?.resourceOverride, variant })
    .PORTAL_POLICY_LINK_TEXT;
};

export const getWarrantyText = (
  settings: CustomerPortalAppSettings | undefined,
) => {
  if (teamOffersWarranties(settings)) {
    return getResource({ overrides: settings?.resourceOverride })
      .PORTAL_WARRANTY_LINK_TEXT;
  }
  return "";
};

export const getLoginGiftText = (
  settings: CustomerPortalAppSettings | undefined,
) => {
  if (settings?.returnPortalGiftButtonText) {
    return settings.returnPortalGiftButtonText;
  }
  const returns = teamOffersReturns(settings);
  const packageProtection = teamOffersPackageProtection(settings);
  const variant =
    returns && packageProtection
      ? "return_claim"
      : packageProtection
        ? "claim"
        : "return";

  return getResource({ overrides: settings?.resourceOverride, variant })
    .PORTAL_GIFT_BUTTON_TEXT;
};

export function getSummarySubtext(
  settings: CustomerPortalAppSettings,
  paymentOwed: number,
  freeNewOrderShipping: boolean,
  totalStoreCredit: number,
  totalRefund: number,
  returnType: ReturnType,
) {
  if (
    returnType === ReturnTypeEnum.CLAIM &&
    settings.theme?.claimSummarySubtext
  ) {
    return settings.theme.claimSummarySubtext;
  }
  if (
    returnType === ReturnTypeEnum.RETURN &&
    settings.theme?.returnSummarySubtext
  ) {
    return settings.theme.returnSummarySubtext;
  }
  // TODO: add recycle summary subtext
  if (
    (returnType === ReturnTypeEnum.WARRANTY ||
      returnType === ReturnTypeEnum.RECYCLE) &&
    settings.theme?.warrantySummarySubtext
  ) {
    return settings.theme.warrantySummarySubtext;
  }

  let refundText = `Once your ${returnType} is processed`;

  if (paymentOwed > 0 || !freeNewOrderShipping) {
    refundText += ", you will receive an email to pay for ";
    if (paymentOwed <= 0) {
      refundText += "the shipping for ";
    }
    refundText += "your new order";
  } else if (totalStoreCredit > 0) {
    refundText += ", you will receive an email with your store credit";
  } else if (totalRefund > 0) {
    refundText += ", you will receive a refund to your original payment method";
  }
  refundText += ".";

  return refundText;
}
